import { Link } from 'react-router-dom';
import { blogPosts, blogCategories } from '@/mocks/blogPosts';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

interface BlogSidebarProps {
  currentSlug?: string;
}

export default function BlogSidebar({ currentSlug }: BlogSidebarProps) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language.startsWith('ar');
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredCategories = blogCategories.filter((c) => c.slug !== 'all');

  const recentPosts = blogPosts
    .filter((p) => p.slug !== currentSlug)
    .slice(0, 3);

  return (
    <aside className="space-y-8">
      {/* Categories */}
      <div className="bg-white rounded-xl border border-background-200/60 p-5">
        <h4 className="text-sm font-bold text-foreground-950 mb-4">
          {t('blog.categories')}
        </h4>
        <div className="flex flex-col gap-1.5">
          <button
            onClick={() => setActiveCategory('all')}
            className={`text-left text-sm px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeCategory === 'all'
                ? 'bg-primary-100 text-primary-700 font-semibold'
                : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
            }`}
          >
            {t('blog.all_posts')}
          </button>
          {filteredCategories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setActiveCategory(cat.slug)}
              className={`text-left text-sm px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat.slug
                  ? 'bg-primary-100 text-primary-700 font-semibold'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              <span>{isAr ? cat.nameAr : cat.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Posts */}
      <div className="bg-white rounded-xl border border-background-200/60 p-5">
        <h4 className="text-sm font-bold text-foreground-950 mb-4">
          {t('blog.recent_posts')}
        </h4>
        <div className="flex flex-col gap-4">
          {recentPosts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="flex gap-3 group"
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
                <img
                  src={post.imageUrl}
                  alt={post.imageAlt}
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                  {post.title}
                </p>
                <p className="text-xs text-foreground-500 mt-1">{post.date}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-primary-500 rounded-xl p-5 text-center">
        <p className="text-sm font-bold text-background-50 mb-2">
          {t('blog.cta_title')}
        </p>
        <p className="text-xs text-background-50/80 mb-4">
          {t('blog.cta_text')}
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-1.5 bg-background-50 text-primary-600 px-4 py-2 rounded-full text-xs font-semibold hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          {t('blog.cta_button')}
          <i className="ri-arrow-right-line text-xs"></i>
        </Link>
      </div>
    </aside>
  );
}